import { cn } from '@/lib/cn'
import { X } from 'lucide-react'
import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Open overlays, innermost last.
 *
 * Dialogs nest: a lookup dialog opens on top of the ingredient form it belongs
 * to, and the recipe list's drawer hosts its own dialogs. Every one of them
 * listens for Escape on `document`, so without this the lowest one would hear
 * it too and the whole stack would close on a single keypress — throwing away
 * whatever the user had typed underneath.
 */
const openOverlays: string[] = []

function isTopmost(id: string): boolean {
  return openOverlays[openOverlays.length - 1] === id
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}) {
  const panel = useRef<HTMLDivElement>(null)
  const id = useId()

  // Stack membership follows `open` alone. `onClose` is usually an inline
  // arrow, so an effect depending on it re-runs every render and would move
  // this overlay back to the top each time, letting an Escape meant for the
  // one above it fall through.
  useEffect(() => {
    if (!open) return
    openOverlays.push(id)
    return () => {
      const at = openOverlays.lastIndexOf(id)
      if (at >= 0) openOverlays.splice(at, 1)
    }
  }, [open, id])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isTopmost(id)) onClose()
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panel.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose, id])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-ink/50 backdrop-blur-[2px]"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative flex max-h-[92dvh] w-full flex-col overflow-hidden bg-surface shadow-overlay',
          'rounded-t-2xl sm:rounded-[var(--radius-card)]',
          size === 'sm' && 'sm:max-w-md',
          size === 'md' && 'sm:max-w-xl',
          size === 'lg' && 'sm:max-w-3xl',
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink">{title}</h2>
            {description ? (
              <p className="mt-0.5 text-micro text-ink-faint">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-m-1 rounded-[var(--radius-field)] p-1 text-ink-faint hover:bg-surface-sunken hover:text-ink"
          >
            <X size={18} />
          </button>
        </div>
        <div className="scroll-slim flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-line bg-surface-sunken px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

/** Right-hand drawer. Full screen on small viewports. */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  const id = useId()

  useEffect(() => {
    if (!open) return
    openOverlays.push(id)
    return () => {
      const at = openOverlays.lastIndexOf(id)
      if (at >= 0) openOverlays.splice(at, 1)
    }
  }, [open, id])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isTopmost(id)) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, id])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close panel"
        onClick={onClose}
        className="absolute inset-0 bg-ink/50"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col bg-surface shadow-overlay"
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-ink">{title}</h2>
            {subtitle ? (
              <p className="mt-0.5 truncate text-xs text-ink-faint">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-m-1 rounded-[var(--radius-field)] p-1 text-ink-faint hover:bg-surface-sunken hover:text-ink"
          >
            <X size={18} />
          </button>
        </div>
        <div className="scroll-slim flex-1 overflow-y-auto">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-line bg-surface-sunken px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = 'Confirm',
  destructive = false,
  loading = false,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  body: ReactNode
  confirmLabel?: string
  destructive?: boolean
  loading?: boolean
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-[var(--radius-field)] px-3 text-sm text-ink-soft hover:bg-line/60"
          >
            Cancel
          </button>
          <button
            type="button"
            data-autofocus
            disabled={loading}
            onClick={onConfirm}
            className={cn(
              'h-9 rounded-[var(--radius-field)] px-3 text-sm font-medium disabled:opacity-50',
              destructive
                ? 'bg-critical text-on-critical hover:bg-critical/90'
                : 'bg-accent text-on-accent hover:bg-accent-hover',
            )}
          >
            {loading ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-sm text-ink-soft">{body}</div>
    </Dialog>
  )
}